/**
 * Event content translation utilities.
 *
 * Maps raw DB enum values to i18n phrase keys for Tier 1 translation,
 * and provides the client-side hook for Tier 2 on-demand translation.
 */

// --- Tier 1: Enum value → UI phrase key mappings ---

/** Maps business_impact DB values to ui.bi_* phrase keys. */
const BI_PHRASE_MAP: Record<string, string> = {
  'open': 'ui.bi_open',
  'closed': 'ui.bi_closed',
  'reduced': 'ui.bi_reduced',
};

/** Maps planning_lead_time DB values to ui.plt_* phrase keys. */
const PLT_PHRASE_MAP: Record<string, string> = {
  'Start 1 week before': 'ui.plt_1w',
  'Start 2 weeks before': 'ui.plt_2w',
  'Start 3 weeks before': 'ui.plt_3w',
  'Start 1 month before': 'ui.plt_1mo',
  'Start 2 months before': 'ui.plt_2mo',
  'Start 3 months before': 'ui.plt_3mo',
  'Start 4 months before': 'ui.plt_4mo',
  'Start 6 months before': 'ui.plt_6mo',
};

/** Maps calculation_method DB values to ui.calc_* phrase keys. */
const CALC_PHRASE_MAP: Record<string, string> = {
  'lunar': 'ui.calc_lunar',
  'solar': 'ui.calc_solar',
  'lunisolar': 'ui.calc_lunisolar',
  'astronomical': 'ui.calc_astronomical',
  'umm-al-qura': 'ui.calc_umm_al_qura',
  'sighting': 'ui.calc_sighting',
  'fixed': 'ui.calc_fixed',
};

/** Templated notes that match business_impact values — use Tier 1 phrases instead. */
const TEMPLATED_NOTES: Record<string, string> = {
  'Minimal business disruption. Community-level observance.': 'ui.notes_open',
  'Government offices, banks, and most businesses closed. Major consumer spending period.': 'ui.notes_closed',
  'Reduced operations in Muslim-majority areas. Some businesses may close.': 'ui.notes_reduced',
};

/**
 * Returns the i18n phrase key for a business_impact value, or null if not mapped.
 */
export function getBusinessImpactPhraseKey(value: string): string | null {
  return BI_PHRASE_MAP[value] ?? null;
}

/**
 * Returns the i18n phrase key for a planning_lead_time value, or null if not mapped.
 */
export function getPlanningLeadTimePhraseKey(value: string): string | null {
  return PLT_PHRASE_MAP[value] ?? null;
}

/**
 * Returns the i18n phrase key for a calculation_method value, or null if not mapped.
 */
export function getCalcMethodPhraseKey(value: string): string | null {
  return CALC_PHRASE_MAP[value] ?? null;
}

/**
 * Returns the i18n phrase key if notes match a templated pattern, or null for unique notes.
 */
export function getTemplatedNotesPhraseKey(notes: string): string | null {
  return TEMPLATED_NOTES[notes.trim()] ?? null;
}

/**
 * Returns true if the notes value is a templated pattern (can use Tier 1 phrase).
 */
export function isTemplatedNotes(notes: string): boolean {
  return notes.trim() in TEMPLATED_NOTES;
}

// --- Tier 2: Per-event translation types ---

export interface EventTranslation {
  title: string;
  titleLocal: string | null;
  notes: string | null;
  complianceRisk: string | null;
  cached: boolean;
}

/**
 * Fetches on-demand translation for an event via the proxy route.
 * Returns null on error (caller should fall back to English).
 */
export async function fetchEventTranslation(
  eventId: string,
  lang: string,
): Promise<EventTranslation | null> {
  if (lang === 'en') return null;

  try {
    const res = await fetch('/api/translate-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventId, lang }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return {
      title: data.title,
      titleLocal: data.title_local ?? null,
      notes: data.notes ?? null,
      complianceRisk: data.compliance_risk ?? null,
      cached: data.cached ?? false,
    };
  } catch {
    return null;
  }
}
